import React from 'react';
import {
  useResponsive, BottomSheet, Drawer, DrawerHeader, DrawerBody, IconButton,
} from '@parsina/framework';
import { X } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useEscapeCloser } from '../../lib/shortcuts.js';

// «مشاهدهٔ همه» — the full list a record section only shows the head of.
//
// A record rail (core.md §7.5) and the record's own overview (§7.4) clip every
// list: three current medications, three active problems, the last three
// conversations. The rest is not hidden, it is one click away — in a sheet, so
// the reading position in the record underneath is not lost. That is the whole
// contract: the section shows what fits, this shows all of it.
//
// One component, two surfaces, because a right-edge drawer on a phone is a
// panel with a 40px reading column and a bottom sheet on a 1440px monitor is a
// letterbox. Both close on Escape through the app's ONE global `esc` binding
// (src/lib/shortcuts.js) rather than a window listener of their own.

export default function RecordListSheet({
  open,
  onClose,
  title,
  children,
  width = 420,
  'data-testid': testId = 'record-list-sheet',
}) {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  // Registers the closer with the global Esc binding while the sheet is up.
  // The kit's BottomSheet closes itself on Escape too; registering anyway is
  // what makes «Esc closes any sheet» true for the drawer half as well.
  useEscapeCloser(open, onClose);

  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        title={title}
        closeLabel={t('common.actions.close')}
        data-testid={testId}
      >
        {children}
      </BottomSheet>
    );
  }

  return (
    <Drawer open={open} onClose={onClose} width={width}>
      <DrawerHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} data-testid={testId}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: C.textPrimary }}>
            {title}
          </h2>
          <IconButton
            variant="ghost"
            size={36}
            icon={<X size={16} color={CT.mute} aria-hidden />}
            label={t('common.actions.close')}
            onClick={onClose}
            style={{ marginInlineStart: 'auto' }}
            data-testid={`${testId}-close`}
          />
        </div>
      </DrawerHeader>
      <DrawerBody>{children}</DrawerBody>
    </Drawer>
  );
}
